import type { CancelHandle, UploadParams } from '../type';
import {
  assertBucketName,
  assertFile,
  createProgressSnapshot,
  getUploadConcurrency,
  getUploadPartSize,
  resolveUploadFileName,
  runWithConcurrency,
  shouldUseMultipartUpload,
} from '../utils/utils';

interface BaiduBosSdkLike {
  BosClient: new (options: Record<string, unknown>) => BaiduBosClient;
  MimeType?: {
    guess: (fileName: string) => string;
  };
  Q?: {
    defer: () => {
      resolve: (value: unknown) => void;
      reject: (error: unknown) => void;
      promise: unknown;
    };
  };
}

interface BaiduBosClient {
  on(eventName: 'progress', callback: (event: BaiduBosProgress) => void): void;
  putObjectFromBlob(
    bucketName: string,
    key: string,
    blob: Blob,
    options?: Record<string, unknown>
  ): Promise<unknown>;
  initiateMultipartUpload(
    bucketName: string,
    key: string,
    options?: Record<string, unknown>
  ): Promise<BaiduBosMultipartInitResponse>;
  uploadPartFromBlob(
    bucketName: string,
    key: string,
    uploadId: string,
    partNumber: number,
    partSize: number,
    blob: Blob
  ): Promise<BaiduBosPartResponse>;
  completeMultipartUpload(
    bucketName: string,
    key: string,
    uploadId: string,
    partList: BaiduBosPart[]
  ): Promise<unknown>;
  abortMultipartUpload?(
    bucketName: string,
    key: string,
    uploadId: string
  ): Promise<unknown> | unknown;
  createSignature?: (
    credentials: unknown,
    httpMethod: string,
    path: string,
    params: Record<string, unknown>,
    headers: Record<string, unknown>
  ) => unknown;
}

interface BaiduBosProgress {
  lengthComputable?: boolean;
  loaded?: number;
  total?: number;
}

interface BaiduBosMultipartInitResponse {
  body?: {
    uploadId?: string;
  };
}

interface BaiduBosPartResponse {
  http_headers?: Record<string, string>;
  httpHeaders?: Record<string, string>;
  headers?: Record<string, string>;
  body?: {
    eTag?: string;
    etag?: string;
  };
}

interface BaiduBosPartTask {
  partNumber: number;
  start: number;
  end: number;
  size: number;
}

interface BaiduBosPart {
  partNumber: number;
  eTag: string;
}

export function upload(
  instance: unknown,
  params: UploadParams,
  bucketName?: string
): Promise<unknown> {
  const bos = instance as BaiduBosClient;
  const file = assertFile(params.file);
  const resolvedBucketName = assertBucketName(bucketName);
  const fileName = resolveUploadFileName(params);
  const isMultipart = shouldUseMultipartUpload(file, params.maxSize);

  if (isMultipart) {
    return uploadMultipart(bos, file, fileName, resolvedBucketName, params);
  }

  return uploadSimple(bos, file, fileName, resolvedBucketName, params);
}

function uploadSimple(
  bos: BaiduBosClient,
  file: File,
  fileName: string,
  bucketName: string,
  params: UploadParams
): Promise<unknown> {
  bos.on('progress', (event) => {
    if (event.lengthComputable === false) {
      return;
    }

    params.onUploadProgress?.(
      createProgressSnapshot(event.loaded ?? 0, event.total ?? file.size)
    );
  });

  return bos.putObjectFromBlob(bucketName, fileName, file, {
    'Content-Type': params.contentType || file.type || undefined,
    ...(params.sdkUploadParams ?? {}),
  });
}

async function uploadMultipart(
  bos: BaiduBosClient,
  file: File,
  fileName: string,
  bucketName: string,
  params: UploadParams
): Promise<unknown> {
  const partSize = getUploadPartSize(params.partSize);
  const concurrency = getUploadConcurrency(params.queueLength);
  const tasks = createMultipartTasks(file, partSize);
  const partList: BaiduBosPart[] = [];
  const options = {
    'Content-Type': params.contentType || file.type || undefined,
    ...(params.sdkUploadParams ?? {}),
  };

  let uploadId = '';
  let uploadedBytes = 0;
  let canceled = false;

  const cancelHandle: CancelHandle = {
    instance: null,
    cancel() {
      canceled = true;
      if (uploadId && bos.abortMultipartUpload) {
        void bos.abortMultipartUpload(bucketName, fileName, uploadId);
      }
    },
  };
  params.onCancel?.(cancelHandle);

  try {
    const initResult = await bos.initiateMultipartUpload(
      bucketName,
      fileName,
      options
    );
    uploadId = initResult.body?.uploadId ?? '';
    if (!uploadId) {
      throw new Error(
        'ObjectStorage: failed to initiate Baidu BOS multipart upload'
      );
    }

    await runWithConcurrency(tasks, concurrency, async (task) => {
      if (canceled) {
        throw new Error('ObjectStorage: Baidu BOS multipart upload canceled');
      }

      const blob = file.slice(task.start, task.end);
      const result = await bos.uploadPartFromBlob(
        bucketName,
        fileName,
        uploadId,
        task.partNumber,
        task.size,
        blob
      );

      partList.push({
        partNumber: task.partNumber,
        eTag: resolvePartETag(result),
      });
      uploadedBytes += task.size;
      params.onUploadProgress?.(
        createProgressSnapshot(uploadedBytes, file.size)
      );
    });

    if (canceled) {
      throw new Error('ObjectStorage: Baidu BOS multipart upload canceled');
    }

    return bos.completeMultipartUpload(
      bucketName,
      fileName,
      uploadId,
      partList.sort((a, b) => a.partNumber - b.partNumber)
    );
  } catch (error) {
    if (uploadId && bos.abortMultipartUpload) {
      await bos.abortMultipartUpload(bucketName, fileName, uploadId);
    }
    throw error;
  }
}

function createMultipartTasks(
  file: File,
  partSize: number
): BaiduBosPartTask[] {
  const tasks: BaiduBosPartTask[] = [];
  let start = 0;
  let partNumber = 1;

  while (start < file.size) {
    const end = Math.min(start + partSize, file.size);
    tasks.push({
      partNumber,
      start,
      end,
      size: end - start,
    });
    start = end;
    partNumber += 1;
  }

  return tasks;
}

function resolvePartETag(response: BaiduBosPartResponse): string {
  const headers =
    response.http_headers ?? response.httpHeaders ?? response.headers;
  const eTag =
    headers?.etag ??
    headers?.ETag ??
    response.body?.eTag ??
    response.body?.etag;

  if (!eTag) {
    throw new Error('ObjectStorage: failed to resolve Baidu BOS part ETag');
  }

  return eTag;
}

export function createBaiduBosInstance<TInstance>(
  sdkObject: unknown,
  config: {
    accessKey?: string;
    secretKey?: string;
    endPoint: string;
    signatureUrl?: string;
    sdkOtherParams?: Record<string, unknown>;
  }
): TInstance {
  const sdk = requireBaiduBosSdk(sdkObject);
  const client = new sdk.BosClient({
    endpoint: config.endPoint,
    credentials: {
      ak: config.accessKey,
      sk: config.secretKey,
    },
    ...(config.sdkOtherParams ?? {}),
  });

  if (config.signatureUrl) {
    client.createSignature = createServerSignature(sdk, config.signatureUrl);
  }

  return client as TInstance;
}

function createServerSignature(
  sdk: BaiduBosSdkLike,
  signatureUrl: string
): NonNullable<BaiduBosClient['createSignature']> {
  return (_credentials, httpMethod, path, params, headers) => {
    const defer = sdk.Q?.defer();
    if (!defer) {
      throw new Error('ObjectStorage: Baidu BOS SDK Q.defer is required');
    }

    const request = new XMLHttpRequest();
    request.open('POST', signatureUrl, true);
    request.setRequestHeader('Content-Type', 'application/json');
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        defer.resolve(request.responseText);
        return;
      }

      defer.reject(
        new Error(
          `ObjectStorage: Baidu BOS signature failed with status ${request.status}`
        )
      );
    };
    request.onerror = () => {
      defer.reject(
        new Error('ObjectStorage: Baidu BOS signature network error')
      );
    };
    request.send(JSON.stringify({ httpMethod, path, params, headers }));

    return defer.promise;
  };
}

function requireBaiduBosSdk(sdkObject: unknown): BaiduBosSdkLike {
  if (sdkObject == null || typeof sdkObject !== 'object') {
    throw new Error(`ObjectStorage: sdkObject is required for type "bos"`);
  }

  return sdkObject as BaiduBosSdkLike;
}
